import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePremiumFeature } from '../components/PremiumFeature';
import { 
    Heart,
    Users,
    Brain,
    RefreshCw,
    Shield,
    ChevronRight,
    ChevronDown,
    Sparkles,
    User,
    Compass,
    Wind,
    MessageCircleHeart,
    ArrowLeft,
    ClipboardCheck
} from 'lucide-react';
import { PrimaryButton, SecondaryButton, TertiaryButton } from '../components/brand/BrandButton';
import { BrandCard } from '../components/brand/BrandCard';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { IconContainer } from '../components/brand/BrandIcon';
import { PersistentBottomBar, BottomBarSpacer } from '../components/brand/PersistentBottomBar';

/**
 * RelationshipHub - Guided Assessment Flow
 * 
 * A simple, ADHD-friendly entry point that asks 1-2 questions
 * and recommends ONE tool based on the user's current state.
 * 
 * Flow: Welcome → Situation → (Partner availability if needed) → Recommendation
 */

const STORAGE_KEY = 'relationship_hub_visited';
const RECENT_TOOLS_KEY = 'relationship_recent_tools';
const ASSESSMENT_KEY = 'relationship_assessment_state';
const MAX_RECENT_TOOLS = 3;

// Glass card variant styles - matching Regulation Hub design
const variantStyles = {
    sage: {
        glassClass: 'glass-card-sage',
        iconBg: 'bg-gradient-to-br from-[#2A8585] via-[#1D6B6B] to-[#145555]',
        iconGlow: 'icon-glow-sage',
        accent: 'text-[#1D6B6B]'
    },
    lavender: {
        glassClass: 'glass-card-lavender',
        iconBg: 'bg-gradient-to-br from-[#9AABB8] via-[#7D8E95] to-[#3A9595]',
        iconGlow: 'icon-glow-lavender',
        accent: 'text-[#7D8E95]'
    },
    rose: {
        glassClass: 'glass-card-rose',
        iconBg: 'bg-gradient-to-br from-[#FFD0B0] via-[#FFBB98] to-[#E09060]',
        iconGlow: 'icon-glow-rose',
        accent: 'text-[#FFBB98]'
    },
    blue: {
        glassClass: 'glass-card-blue',
        iconBg: 'bg-gradient-to-br from-[#3A9595] via-[#2A8585] to-[#1D6B6B]',
        iconGlow: 'icon-glow-blue',
        accent: 'text-[#7D8E95]'
    },
    gold: {
        glassClass: 'glass-card-gold',
        iconBg: 'bg-gradient-to-br from-[#FFD0B0] via-[#FFBB98] to-[#E09060]',
        iconGlow: 'icon-glow-gold',
        accent: 'text-[#FFBB98]'
    }
};

// Variant colors for recommendation display
const variantColors = {
    sage: 'bg-[#1D6B6B]/20',
    lavender: 'bg-[#7D8E95]/20',
    rose: 'bg-[#FFBB98]/20',
    blue: 'bg-[#3A9595]/20',
    gold: 'bg-[#FFBB98]/20'
};

const variantIconColors = {
    sage: 'text-[#1D6B6B]',
    lavender: 'text-[#7D8E95]',
    rose: 'text-[#FFBB98]',
    blue: 'text-[#3A9595]',
    gold: 'text-[#FFBB98]'
};

// Tool metadata for displaying recent tools
const TOOL_METADATA = {
    '/relationship-hub/solo-need': { title: 'What Do I Need?', icon: User, variant: 'lavender' },
    '/relationship-hub/break-the-cycle': { title: 'Break the Cycle', icon: RefreshCw, variant: 'lavender' },
    '/relationship-hub/cycles': { title: 'Which Cycle Are We In?', icon: RefreshCw, variant: 'lavender' },
    '/relationship-hub/infinity-loop': { title: 'Infinity Loop', icon: RefreshCw, variant: 'rose' },
    '/relationship-hub/attachment-needs': { title: 'Attachment Needs', icon: Heart, variant: 'rose' },
    '/relationship-hub/check-in': { title: 'Relationship Check-in', icon: ClipboardCheck, variant: 'lavender' },
    '/relationship-hub/repair': { title: 'Repair & Reconnection', icon: MessageCircleHeart, variant: 'rose' },
    '/regulate/what-do-we-need': { title: 'What Do We Need?', icon: Users, variant: 'blue' },
    '/regulate/coregulation': { title: 'Relationship Co-Regulation', icon: MessageCircleHeart, variant: 'blue' },
    '/regulate/partner-mode': { title: 'Real-Time Partner Sync', icon: Users, variant: 'blue' },
    '/regulate/regulation-navigator': { title: 'Regulation Navigator', icon: Compass, variant: 'sage' },
    '/regulate/micro-reset': { title: 'Micro Reset', icon: Wind, variant: 'sage' },
    '/regulate/self-talk': { title: 'Self-Talk Reframes', icon: Heart, variant: 'sage' },
    '/regulate/adhd-triggers': { title: 'ADHD Triggers', icon: Brain, variant: 'lavender' },
    '/regulate/relationship-check': { title: 'Safety Check', icon: Shield, variant: 'rose' }
};

const RelationshipHub = () => {
    const navigate = useNavigate();
    const { canAccess } = useAuth();
    const { checkAccess } = usePremiumFeature();
    
    // Check for browse query parameter
    const urlParams = new URLSearchParams(window.location.search);
    const startInBrowse = urlParams.get('browse') === 'true';
    const resetAssessment = urlParams.get('reset') === 'true';
    
    // Load saved assessment state
    const getSavedAssessment = () => {
        if (resetAssessment) {
            localStorage.removeItem(ASSESSMENT_KEY);
            return null;
        }
        try {
            const saved = localStorage.getItem(ASSESSMENT_KEY);
            if (saved) {
                return JSON.parse(saved);
            }
        } catch (e) {
            return null;
        }
        return null;
    };
    
    const savedAssessment = getSavedAssessment();
    
    // Determine initial step:
    // - If browse param, start in browse
    // - If has saved assessment, show returning user view
    // - Otherwise, start at situation (assessment) directly
    const getInitialStep = () => {
        if (startInBrowse) return 'browse';
        if (savedAssessment && savedAssessment.situation) return 'returning';
        return 'situation'; // Start directly at assessment
    };
    
    // Assessment state
    const [step, setStep] = useState(getInitialStep());
    const [situation, setSituation] = useState(savedAssessment?.situation || null);
    const [partnerAvailable, setPartnerAvailable] = useState(savedAssessment?.partnerAvailable || null);
    const [fadeIn, setFadeIn] = useState(false);
    const [showAllTools, setShowAllTools] = useState(false);
    const [recentTools, setRecentTools] = useState([]);

    // Load recent tools on mount
    useEffect(() => {
        const stored = localStorage.getItem(RECENT_TOOLS_KEY);
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                setRecentTools(parsed.slice(0, MAX_RECENT_TOOLS));
            } catch (e) {
                setRecentTools([]);
            }
        }
        setTimeout(() => setFadeIn(true), 100);
    }, []);

    // Track tool usage
    const trackToolUsage = (path) => {
        const stored = localStorage.getItem(RECENT_TOOLS_KEY);
        let recent = [];
        if (stored) {
            try {
                recent = JSON.parse(stored);
            } catch (e) {
                recent = [];
            }
        }
        // Remove if already exists (to move to front)
        recent = recent.filter(p => p !== path);
        // Add to front
        recent.unshift(path);
        // Keep only MAX_RECENT_TOOLS
        recent = recent.slice(0, MAX_RECENT_TOOLS);
        localStorage.setItem(RECENT_TOOLS_KEY, JSON.stringify(recent));
        setRecentTools(recent);
    };

    // Navigate to tool and track
    const navigateToTool = (path) => {
        trackToolUsage(path);
        navigate(path);
    };

    useEffect(() => {
        setFadeIn(false);
        const timer = setTimeout(() => setFadeIn(true), 50);
        return () => clearTimeout(timer);
    }, [step]);

    // Situation options with variants
    const situationOptions = [
        {
            id: 'conflict',
            label: "We're in conflict",
            description: 'Tension, disconnection, or a difficult moment',
            icon: Heart,
            variant: 'rose'
        },
        {
            id: 'patterns',
            label: 'I want to understand us',
            description: 'Explore our patterns and dynamics',
            icon: RefreshCw,
            variant: 'lavender'
        },
        {
            id: 'regulate',
            label: 'I need to calm down first',
            description: 'Settle my nervous system before anything else',
            icon: Wind,
            variant: 'sage'
        },
        {
            id: 'reconnect',
            label: 'I want to reconnect',
            description: 'Repair and come back together',
            icon: Users,
            variant: 'blue'
        }
    ];

    // Get recommendation based on situation and partner availability
    const getRecommendation = () => {
        const recommendations = {
            // Conflict situations
            'conflict-yes': {
                title: 'What Do We Need Right Now?',
                description: 'A guided conversation tool to help you both slow down and name what you need.',
                path: '/regulate/what-do-we-need',
                icon: Users,
                variant: 'rose',
                premium: false,
                requiredFeature: null,
                fallback: {
                    title: 'Solo: What Do I Need?',
                    description: 'Start by getting clear on your own needs first.',
                    path: '/relationship-hub/solo-need',
                    icon: User,
                    variant: 'lavender'
                }
            },
            'conflict-no': {
                title: 'Solo: What Do I Need?',
                description: "When your partner isn't available, start by understanding your own needs and feelings.",
                path: '/relationship-hub/solo-need',
                icon: User,
                variant: 'lavender'
            },
            
            // Understanding patterns
            'patterns-yes': {
                title: 'Break the Cycle Together',
                description: 'Explore your relationship patterns side by side and find new ways forward.',
                path: '/relationship-hub/break-the-cycle',
                icon: RefreshCw,
                variant: 'lavender'
            },
            'patterns-no': {
                title: 'Break the Cycle (Solo)',
                description: 'Explore your patterns on your own. Understanding starts with you.',
                path: '/relationship-hub/break-the-cycle',
                icon: RefreshCw,
                variant: 'lavender'
            },
            
            // Regulation (no partner question needed)
            'regulate': {
                title: 'Regulation Navigator',
                description: 'Gentle grounding exercises to help your nervous system settle.',
                path: '/regulate/regulation-navigator',
                icon: Compass,
                variant: 'sage'
            },
            
            // Reconnection
            'reconnect-yes': {
                title: 'Relationship Co-Regulation',
                description: 'A gentle, guided way to come back together with softness.',
                path: '/regulate/coregulation',
                icon: MessageCircleHeart,
                variant: 'blue'
            },
            'reconnect-no': {
                title: 'Solo: What Do I Need?',
                description: 'Prepare for reconnection by getting clear on what you need.',
                path: '/relationship-hub/solo-need',
                icon: User,
                variant: 'lavender'
            }
        };

        // Build key based on situation and partner availability
        let key = situation;
        if (situation !== 'regulate' && partnerAvailable !== null) {
            key = `${situation}-${partnerAvailable ? 'yes' : 'no'}`;
        }

        return recommendations[key] || recommendations['regulate'];
    };
    
    // Compute recommendation (for returning users and recommendation step)
    const recommendation = (situation) ? getRecommendation() : null;

    const handleSituationSelect = (id) => {
        setSituation(id);
        // Regulation doesn't need partner question
        if (id === 'regulate') {
            // Save assessment state
            localStorage.setItem(ASSESSMENT_KEY, JSON.stringify({ situation: id, partnerAvailable: null }));
            setTimeout(() => setStep('recommendation'), 300);
        } else {
            setTimeout(() => setStep('partner'), 300);
        }
    };

    const handlePartnerSelect = (available) => {
        setPartnerAvailable(available);
        // Save assessment state
        localStorage.setItem(ASSESSMENT_KEY, JSON.stringify({ situation, partnerAvailable: available }));
        setTimeout(() => setStep('recommendation'), 300);
    };

    const handleStartTool = () => {
        const rec = getRecommendation();
        // Mark as visited
        localStorage.setItem(STORAGE_KEY, 'true');
        
        if (rec.premium && rec.requiredFeature && !canAccess(rec.requiredFeature)) {
            // If premium and locked, try fallback or show paywall
            if (rec.fallback) {
                trackToolUsage(rec.fallback.path);
                navigate(rec.fallback.path);
            } else {
                // Navigate directly - no premium check needed
                trackToolUsage(rec.path);
                navigate(rec.path);
            }
        } else {
            trackToolUsage(rec.path);
            navigate(rec.path);
        }
    };

    const handleBrowseAll = () => {
        localStorage.setItem(STORAGE_KEY, 'true');
        setStep('browse');
    };
    
    const handleNewAssessment = () => {
        // Clear saved assessment and start fresh
        localStorage.removeItem(ASSESSMENT_KEY);
        setSituation(null);
        setPartnerAvailable(null);
        setStep('situation');
    };

    // All tools organized by category
    const allTools = [
        {
            category: 'Assessments',
            icon: ClipboardCheck,
            variant: 'lavender',
            tools: [
                { title: 'Relationship Check-in', path: '/relationship-hub/check-in', description: 'Assess your relationship patterns & get guidance' },
                { title: 'Relationship Safety Check', path: '/regulate/relationship-check', description: 'Assess emotional safety in your relationship' }
            ]
        },
        {
            category: 'Understand Patterns',
            icon: RefreshCw,
            variant: 'lavender',
            tools: [
                { title: 'Infinity Loop', path: '/relationship-hub/infinity-loop', description: 'Map your negative cycles using Emotionally Focused Therapy' },
                { title: 'Break the Cycle', path: '/relationship-hub/break-the-cycle', description: 'Interactive guide to relationship patterns' },
                { title: 'Which Cycle Are We In?', path: '/relationship-hub/cycles', description: 'Identify your current dynamic' }
            ]
        },
        {
            category: 'Solo Reflection',
            icon: User,
            variant: 'rose',
            tools: [
                { title: 'What Do I Need?', path: '/relationship-hub/solo-need', description: 'Gentle self check-in' },
                { title: 'Attachment Needs', path: '/relationship-hub/attachment-needs', description: 'Explore your attachment style' }
            ]
        },
        {
            category: 'Repair & Reconnection',
            icon: MessageCircleHeart,
            variant: 'rose',
            tools: [
                { title: 'Repair & Reconnection', path: '/relationship-hub/repair', description: 'Questions to guide important conversations and achieve repair' }
            ]
        },
        {
            category: 'Together Tools',
            icon: Users,
            variant: 'blue',
            tools: [
                { title: 'What Do We Need?', path: '/regulate/what-do-we-need', description: 'Guided conversation', premium: false, requiredFeature: null },
                { title: 'Relationship Co-Regulation', path: '/regulate/coregulation', description: 'Regulate your emotions and find balance and calm in your relationship' },
                { title: 'Real-Time Partner Sync', path: '/regulate/partner-mode', description: 'Two-device synchronized experience', premium: false, requiredFeature: null }
            ]
        },
        {
            category: 'Need to Regulate First?',
            icon: Wind,
            variant: 'sage',
            isHubLink: true,
            hubPath: '/regulate',
            hubDescription: 'Access all regulation tools including Regulation Navigator, Micro Reset, and more'
        }
    ];

    const handleToolClick = (tool) => {
        // Navigate directly - no premium check needed
        trackToolUsage(tool.path);
        navigate(tool.path);
    };

    // BROWSE ALL TOOLS VIEW
    if (step === 'browse') {
        return (
            <div className="min-h-screen bg-transparent">
                {/* Header */}
                <header className="sticky top-0 z-40 glass-header">
                    <div className="max-w-xl mx-auto px-6 py-4 flex items-center gap-4">
                        <button 
                            onClick={() => setStep('welcome')}
                            className="p-2 -ml-2 rounded-full hover:bg-[#FFBB98]/50 transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5 text-[#7D8E95]" />
                        </button>
                        <Heading as="h1" size="text-lg" className="text-[#1D6B6B]">
                            All Relationship Tools
                        </Heading>
                    </div>
                </header>

                <main className="max-w-xl mx-auto px-6 py-8">
                    <div className="space-y-8">
                        {allTools.map((category, catIndex) => {
                            const catStyle = variantStyles[category.variant] || variantStyles.sage;
                            return (
                                <div key={category.category} className="animate-fade-in" style={{ animationDelay: `${catIndex * 100}ms` }}>
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className={`w-8 h-8 rounded-full ${catStyle.iconBg} flex items-center justify-center ${catStyle.iconGlow}`}>
                                            <category.icon className="w-4 h-4 text-white" />
                                        </div>
                                        <SmallText className="font-semibold text-[#1D6B6B] uppercase tracking-wide text-xs">
                                            {category.category}
                                        </SmallText>
                                    </div>
                                
                                {/* Hub link (e.g., Regulation Hub) */}
                                {category.isHubLink ? (
                                    <button
                                        onClick={() => navigate(category.hubPath)}
                                        className={`w-full ${variantStyles[category.variant].glassClass} rounded-2xl p-5 cursor-pointer card-hover hover-shine
                                                 flex items-center gap-5`}
                                        data-testid="regulation-hub-link"
                                    >
                                        <div className={`w-12 h-12 rounded-xl ${variantStyles[category.variant].iconBg} flex items-center justify-center flex-shrink-0 ${variantStyles[category.variant].iconGlow}`}>
                                            <Wind className="w-6 h-6 text-white" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <BodyText className="font-semibold text-[#1D6B6B]">Go to Regulation Tools</BodyText>
                                            <SmallText className="text-[#2A8585]">{category.hubDescription}</SmallText>
                                        </div>
                                        <ChevronRight className="w-5 h-5 text-[#7D8E95] flex-shrink-0" />
                                    </button>
                                ) : (
                                    <div className="space-y-3">
                                        {category.tools.map((tool, toolIndex) => {
                                            const isLocked = tool.premium && tool.requiredFeature && !canAccess(tool.requiredFeature);
                                            // Cycle through variants for tools in category
                                            const toolVariants = ['sage', 'lavender', 'rose', 'blue', 'gold'];
                                            const toolVariant = toolVariants[toolIndex % toolVariants.length];
                                            const style = variantStyles[toolVariant];
                                            return (
                                                <button
                                                    key={tool.title}
                                                    onClick={() => handleToolClick(tool)}
                                                    className={`w-full ${style.glassClass} rounded-2xl p-5 cursor-pointer card-hover hover-shine
                                                             flex items-center gap-5`}
                                                >
                                                    <div className="flex-1 min-w-0">
                                                        <BodyText className="font-semibold text-[#1D6B6B]">{tool.title}</BodyText>
                                                        <SmallText className="text-[#2A8585]">{tool.description}</SmallText>
                                                    </div>
                                                    {isLocked && <Sparkles className="w-4 h-4 text-[#7D8E95] flex-shrink-0" />}
                                                    <ChevronRight className="w-4 h-4 text-[#7D8E95] flex-shrink-0" />
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                            );
                        })}
                    </div>

                    {/* Back to assessment */}
                    <div className="mt-10 text-center">
                        <TertiaryButton onClick={handleNewAssessment} className="text-[#7D8E95]">
                            Start guided assessment
                        </TertiaryButton>
                    </div>
                </main>
            </div>
        );
    }

    // GUIDED ASSESSMENT FLOW
    return (
        <div className="min-h-screen bg-transparent">
            {/* Header with back button */}
            <header className="sticky top-0 z-40 glass-header">
                <div className="max-w-xl mx-auto px-6 py-4 flex items-center gap-4">
                    <button 
                        onClick={() => navigate('/home?tab=relationships')}
                        className="p-2 -ml-2 rounded-full hover:bg-[#FFBB98]/40 transition-colors text-[#7D8E95] hover:text-[#1D6B6B]"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <Heading as="h1" size="text-lg" className="text-[#1D6B6B]">
                        Relationship Hub
                    </Heading>
                </div>
            </header>
            
            <main className="max-w-md mx-auto px-6 py-8 min-h-screen flex flex-col">
                
                {/* RETURNING USER - Shows their last recommendation */}
                {step === 'returning' && recommendation && (
                    <div className={`flex-1 flex flex-col justify-center transition-opacity duration-500 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
                        <div className="flex justify-center mb-6">
                            <div className="w-16 h-16 rounded-xl bg-[#1D6B6B]/10 flex items-center justify-center">
                                <Sparkles className="w-8 h-8 text-[#1D6B6B]" />
                            </div>
                        </div>
                        <div className="text-center mb-6">
                            <Heading level={1} className="text-[#1D3D3D] text-xl md:text-2xl font-semibold mb-3">
                                Welcome back
                            </Heading>
                            <BodyText className="text-[#5A7A7A]">
                                Based on your last check-in, here's what might help:
                            </BodyText>
                        </div>
                        
                        {/* Last Recommendation Card */}
                        <button
                            onClick={handleStartTool}
                            className={`w-full ${variantStyles[recommendation.variant || 'sage'].glassClass} rounded-2xl p-5 cursor-pointer card-hover hover-shine mb-4`}
                            data-testid="continue-recommendation"
                        >
                            <div className="flex items-center gap-5">
                                <div className={`w-12 h-12 rounded-xl ${variantStyles[recommendation.variant || 'sage'].iconBg} flex items-center justify-center flex-shrink-0 ${variantStyles[recommendation.variant || 'sage'].iconGlow}`}>
                                    <recommendation.icon className="w-6 h-6 text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-[#1D6B6B] mb-0.5">{recommendation.title}</div>
                                    <div className="text-[#2A8585] text-sm">{recommendation.description}</div>
                                </div>
                                <ChevronRight className="w-5 h-5 text-[#7D8E95] flex-shrink-0" />
                            </div>
                        </button>
                        
                        {/* Recent Tools */}
                        {recentTools.length > 0 && (
                            <div className="mb-6">
                                <SmallText className="text-[#7D8E95] font-medium mb-3 block">
                                    Your recent tools
                                </SmallText>
                                <div className="space-y-3">
                                    {recentTools.slice(0, 2).map((path, index) => {
                                        const tool = TOOL_METADATA[path];
                                        if (!tool) return null;
                                        const ToolIcon = tool.icon;
                                        const style = variantStyles[tool.variant || 'sage'];
                                        return (
                                            <button
                                                key={path}
                                                onClick={() => navigateToTool(path)}
                                                className={`w-full ${style.glassClass} rounded-2xl p-4 cursor-pointer card-hover hover-shine
                                                         flex items-center gap-4`}
                                                data-testid={`recent-tool-${index}`}
                                            >
                                                <div className={`w-10 h-10 rounded-xl ${style.iconBg} flex items-center justify-center flex-shrink-0 ${style.iconGlow}`}>
                                                    <ToolIcon className="w-5 h-5 text-white" />
                                                </div>
                                                <BodyText className="font-semibold text-[#1D6B6B] flex-1">{tool.title}</BodyText>
                                                <ChevronRight className="w-4 h-4 text-[#7D8E95]" />
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        
                        {/* Options */}
                        <div className="space-y-3 mt-auto">
                            <button 
                                onClick={handleNewAssessment}
                                className="w-full py-4 px-6 rounded-2xl bg-[#1D6B6B] text-white font-semibold
                                         hover:bg-[#145555] transition-all shadow-md flex items-center justify-center gap-2"
                                data-testid="new-assessment-btn"
                            >
                                Something different today
                                <ChevronRight className="w-5 h-5" />
                            </button>
                            <button 
                                onClick={handleBrowseAll}
                                className="w-full py-3 text-[#7D8E95] hover:text-[#1D6B6B] transition-colors text-sm"
                                data-testid="browse-all-btn"
                            >
                                Browse all tools
                            </button>
                        </div>
                    </div>
                )}

                {/* SITUATION */}
                {step === 'situation' && (
                    <div className={`flex-1 flex flex-col transition-opacity duration-500 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
                        {/* Icon and header */}
                        <div className="flex justify-center mb-6 pt-4">
                            <div className="w-16 h-16 rounded-xl bg-[#1D6B6B]/10 flex items-center justify-center">
                                <Heart className="w-8 h-8 text-[#1D6B6B]" />
                            </div>
                        </div>
                        <div className="text-center mb-8">
                            <SmallText className="text-[#1D6B6B] font-medium mb-2">What's happening?</SmallText>
                            <Heading level={2} className="text-[#1D3D3D] text-xl md:text-2xl font-semibold leading-relaxed mb-2">
                                What brings you here right now?
                            </Heading>
                            <BodyText className="text-[#5A7A7A] text-sm">
                                Let's find the right tool for this moment.
                            </BodyText>
                        </div>
                        <div className="space-y-4 flex-1">
                            {situationOptions.map((option, index) => {
                                const style = variantStyles[option.variant];
                                return (
                                    <button
                                        key={option.id}
                                        onClick={() => handleSituationSelect(option.id)}
                                        className={`w-full ${style.glassClass} rounded-2xl p-5 cursor-pointer card-hover hover-shine brand-slide-up`}
                                        style={{ animationDelay: `${index * 50}ms` }}
                                        data-testid={`situation-${option.id}`}
                                    >
                                        <div className="flex items-center gap-5">
                                            <div className={`w-12 h-12 rounded-xl ${style.iconBg} flex items-center justify-center flex-shrink-0 ${style.iconGlow}`}>
                                                <option.icon className="w-6 h-6 text-white" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold text-[#1D6B6B] mb-0.5">{option.label}</div>
                                                <div className="text-[#2A8585] text-sm">{option.description}</div>
                                            </div>
                                            <ChevronRight className="w-5 h-5 text-[#7D8E95] flex-shrink-0" />
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                        <div className="mt-6 text-center">
                            <button 
                                onClick={handleBrowseAll} 
                                className="text-sm text-[#7D8E95] hover:text-[#1D6B6B] transition-colors"
                            >
                                Browse all tools instead
                            </button>
                        </div>
                    </div>
                )}

                {/* PARTNER AVAILABILITY */}
                {step === 'partner' && (
                    <div className={`flex-1 flex flex-col transition-opacity duration-500 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
                        {/* Icon and header */}
                        <div className="flex justify-center mb-6 pt-4">
                            <div className="w-16 h-16 rounded-xl bg-[#1D6B6B]/10 flex items-center justify-center">
                                <Users className="w-8 h-8 text-[#1D6B6B]" />
                            </div>
                        </div>
                        <div className="text-center mb-8">
                            <SmallText className="text-[#1D6B6B] font-medium mb-2">One more thing</SmallText>
                            <Heading level={2} className="text-[#1D3D3D] text-xl md:text-2xl font-semibold leading-relaxed mb-2">
                                Is your partner available right now?
                            </Heading>
                            <BodyText className="text-[#5A7A7A] text-sm">
                                This helps us suggest the right tool.
                            </BodyText>
                        </div>
                        <div className="space-y-4 flex-1">
                            <button
                                onClick={() => handlePartnerSelect(true)}
                                className={`w-full ${variantStyles.blue.glassClass} rounded-2xl p-5 cursor-pointer card-hover hover-shine`}
                                data-testid="partner-yes"
                            >
                                <div className="flex items-center gap-5">
                                    <div className={`w-12 h-12 rounded-xl ${variantStyles.blue.iconBg} flex items-center justify-center flex-shrink-0 ${variantStyles.blue.iconGlow}`}>
                                        <Users className="w-6 h-6 text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-[#1D6B6B] mb-0.5">Yes, they're here</div>
                                        <div className="text-[#2A8585] text-sm">We can do this together</div>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-[#7D8E95] flex-shrink-0" />
                                </div>
                            </button>
                            <button
                                onClick={() => handlePartnerSelect(false)}
                                className={`w-full ${variantStyles.lavender.glassClass} rounded-2xl p-5 cursor-pointer card-hover hover-shine`}
                                data-testid="partner-no"
                            >
                                <div className="flex items-center gap-5">
                                    <div className={`w-12 h-12 rounded-xl ${variantStyles.lavender.iconBg} flex items-center justify-center flex-shrink-0 ${variantStyles.lavender.iconGlow}`}>
                                        <User className="w-6 h-6 text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-[#1D6B6B] mb-0.5">No, just me</div>
                                        <div className="text-[#2A8585] text-sm">I'll work on this solo for now</div>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-[#7D8E95] flex-shrink-0" />
                                </div>
                            </button>
                        </div>
                        <div className="mt-6 text-center">
                            <button 
                                onClick={() => setStep('situation')} 
                                className="text-sm text-[#7D8E95] hover:text-[#1D6B6B] transition-colors"
                            >
                                Back
                            </button>
                        </div>
                    </div>
                )}

                {/* RECOMMENDATION */}
                {step === 'recommendation' && recommendation && (
                    <div className={`flex-1 flex flex-col transition-opacity duration-500 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
                        <div className="flex justify-center gap-2 mb-8 pt-4">
                            <div className="w-8 h-1 rounded-full bg-[#FFBB98]" />
                            <div className="w-8 h-1 rounded-full bg-[#FFBB98]" />
                            <div className="w-8 h-1 rounded-full bg-[#FFBB98]" />
                        </div>
                        <div className="flex-1 flex flex-col justify-center">
                            <div className="text-center mb-6">
                                <SmallText className="text-[#1D6B6B] font-medium mb-3">Here's what might help</SmallText>
                            </div>
                            <div className="glass-card rounded-2xl p-5">
                                <div className={`w-16 h-16 rounded-full ${variantColors[recommendation.variant]} flex items-center justify-center mx-auto mb-6`}>
                                    <recommendation.icon className={`w-7 h-7 ${variantIconColors[recommendation.variant]}`} />
                                </div>
                                <Heading level={2} className="text-[#1D6B6B] text-xl md:text-2xl font-medium mb-3">
                                    {recommendation.title}
                                </Heading>
                                <BodyText className="text-[#7D8E95] leading-relaxed">
                                    {recommendation.description}
                                </BodyText>
                                {recommendation.premium && !canAccess(recommendation.requiredFeature) && recommendation.fallback && (
                                    <div className="mt-4 p-3 rounded-xl bg-[#7D8E95]/30">
                                        <SmallText className="text-[#7D8E95]">
                                            This is a premium tool. We'll start you with the solo version.
                                        </SmallText>
                                    </div>
                                )}
                            </div>
                            <BottomBarSpacer height="extended" />
                        </div>
                    </div>
                )}
                
                {step === 'recommendation' && recommendation && (
                    <PersistentBottomBar>
                        <PrimaryButton 
                            onClick={handleStartTool} 
                            className="w-full" 
                            size="action" 
                            data-testid="start-tool-btn"
                        >
                            Let's begin
                            <ChevronRight className="w-4 h-4 ml-1.5" />
                        </PrimaryButton>
                        <div className="text-center mt-1 space-y-1">
                            <button 
                                onClick={() => setStep('situation')}
                                className="text-sm text-[#7D8E95] hover:text-[#7D8E95] transition-colors block w-full"
                            >
                                Try different options
                            </button>
                            <button 
                                onClick={handleBrowseAll}
                                className="text-sm text-[#7D8E95] hover:text-[#7D8E95] transition-colors block w-full"
                                data-testid="browse-all-tools-btn"
                            >
                                Browse all tools
                            </button>
                        </div>
                    </PersistentBottomBar>
                )}
            </main>
        </div>
    );
};

export default RelationshipHub;
